<template>
	<div class="tab-pane" :class="{ active: defaultTab, show: defaultTab }" :id="id">
		<div class="p-4 b-b _600 nav-title">Assessment Details</div>
		<div role="form" class="col-12 col-lg-8">
			<div class="row m-0 pt-4">
				<div class="col-12">
					<div class="form-group" :class="{ invalid: !valid.field('name') }">
						<label>Assessment Name</label>
						<input type="text" class="form-control" v-model="assessment.name" />
					</div>
				</div>
				<div class="col-12 col-sm-10">
					<div class="form-group" :class="{ invalid: !(valid.field('ref') && valid.field('ref_char')) }">
						<label>Reference ID</label>
						<input type="text" class="form-control" v-model="assessment.ref_id" />
						<small class="form-text text-muted">Reference ID must be unique within this tenant</small>
					</div>
				</div>
				<div class="col-2">
					<label class>Active:</label>
					<div class="form-group">
						<label class="ui-switch ui-switch-md theme-accent">
							<input type="checkbox" v-model="assessment.active" />
							<i></i>
						</label>
					</div>
				</div>

				<div class="col-12 col-sm-6">
					<div class="form-group">
						<label>Content Area</label>
						<v-select
							class="v-select-apply"
							:options="contentAreas"
							v-model="selectedContentArea"
							@input="updateContentAreaID"
							label="name"
						></v-select>
					</div>
				</div>

				<div class="col-sm-6 col-12">
					<div class="form-group">
						<label>Grades</label>
						<v-select :options="grades" v-model="sGrades" multiple label="name"></v-select>
					</div>
				</div>

				<div class="col-12">
					<div class="form-group">
						<label>Description</label>
						<RichText
							:text.sync="assessment.description"
							:initial="originalDesc"
							:options="{ height: 140 }"
							:refresh="refresh"
							class="border"
						></RichText>
					</div>
				</div>

				<div class="col-8">
					<div class="form-group">
						<label>Items</label>
						<div class="d-flex flex-row">
							<config-item-search v-model="selectedItem" class="flex mr-2"></config-item-search>
							<button @click="addItem()" class="btn theme">Add</button>
						</div>
						<draggable
							v-model="assessment.assessment_items"
							:options="{ handle: '.drag-handle' }"
							@sort="itemsSorted"
						>
							<li
								class="list-item hover"
								v-for="item in assessment.assessment_items"
								:key="item.sequence"
							>
								<div class="m-auto px-2 drag-handle flex-fixed pointer">
									<i class="far fa-sort text-muted"></i>
								</div>
								<a>
									<span class="w-30 circle theme avatar" style="width:25px; height:25px;">
										<span>{{ item.sequence }}</span>
									</span>
								</a>
								<div class="list-body align-middle">
									<div class="align-middlea">
										<a>{{ item.item.name }}</a>
										<br />
									</div>
								</div>
							</li>
						</draggable>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.margin-overlap {
	margin-right: -2.5rem;
}
</style>

<script>
import EditPane from "@/components/EditPane";
import SaveOptionalModal from "@/components/SaveOptionalModal";
import ConfigSelect from "@/components/ConfigSelect";
import RichText from "@/components/RichText";

import AssessmentService from "@/services/AssessmentService";
import ContentAreaService from "@/services/ContentAreaService";
import GradeService from "@/services/GradeService";
import ValidationService from "@/services/ValidationService";
import Notie from "@/services/NotieService";
import fs from "@/services/FormatService";
import BB from "bluebird";
import Utils from "@/services/Utils";
import draggable from "vuedraggable";
import ConfigItemSearch from "@/components/ConfigItemSearch";

export default {
	name: "AssessmentEditDetails",

	props: ["id", "assessment", "defaultTab", "valid"],

	components: {
		EditPane,
		SaveOptionalModal,
		ConfigSelect,
		ConfigItemSearch,
		RichText,
		draggable
	},

	data() {
		return {
			fs: fs,
			assessment: {
				loaded: 0
			},

			contentAreas: [],
			selectedContentArea: null,
			selectedItem: null,
			originalDesc: "",
			refresh: null,

			grades: [],
			sGrades: null,

			dirty: false,
			valid: {},
			saveOptModal: false,
			saveOptNext: () => {},
			saveOptCancel: () => {}
		};
	},

	computed: {
		sGradeIDs() {
			return _.map(this.sGrades, "id");
		}
	},

	created() {},

	watch: {
		sGradeIDs(newVal) {
			let oldVal = this.assessment.grade_ids;
			if (oldVal == null) {
				oldVal = newVal.slice(0, newVal.length - 1);
			}
			let same = true;
			if (newVal.length == oldVal.length) {
				for (let i = 0; i < newVal.length; i++) {
					if (newVal[i] != oldVal[i]) {
						same = false;
					}
				}
			} else {
				same = false;
			}

			if (!same) {
				this.assessment.grade_ids = newVal;
			}
		}
	},

	beforeRouteLeave(to, from, next) {
		if (this.dirty) {
			this.saveOptNext = () => {
				next();
			};
			this.saveOptCancel = () => {
				next(false);
			};
			this.saveOptModal = true;
		} else {
			next();
		}
	},

	methods: {
		initValidation() {
			this.valid = ValidationService.newValidator({
				name: {
					group: "details",
					errorMsg: "You must assign a name",
					func: () => {
						return this.assessment && this.assessment.name && this.assessment.name != "";
					}
				},
				ref: {
					group: "details",
					errorMsg: "You must assign a reference ID",
					func: () => {
						return this.assessment && this.assessment.ref_id && this.assessment.ref_id != "";
					}
				},
				ref_char: {
					group: "details",
					errorMsg:
						"Reference IDs may not contain spaces or any of the following characters: ! # % ^ * ( )  / [ ] { } < > ? | ' \" :",
					func: () => {
						return this.assessment && this.assessment.ref_id && fs.isGoodRefID(this.assessment.ref_id);
					}
				}
			});
		},

		blankAssessment() {
			return {
				loaded: 0
			};
		},

		watchChanges() {
			if (this.unwatch) {
				this.unwatch();
			}
			this.unwatch = this.$watch(
				"assessment",
				(newc, old) => {
					this.dirty = true;
				},
				{ deep: true }
			);
		},

		updateContentAreaID(contentArea) {
			this.assessment.content_area_id = (contentArea && contentArea.id) || "";
		},

		saveAssessment() {
			return AssessmentService.saveAssessment(this.assessment)
				.then(resp => {
					Notie.info("Assessment Saved");
					if (this.assessment.id != resp.data.id) {
						this.$router.replace("/assessments/" + resp.data.id);
					}
					this.loadData();
				})
				.catch(err => {
					console.log(err);
					Notie.error("Failed to save assessment", err);
				});
		},

		addItem() {
			this.assessment.assessment_items.push({
				sequence: this.assessment.assessment_items.length + 1,
				item_id: this.selectedItem.id,
				item: this.selectedItem
			});
			this.dirty = true;
		},

		itemsSorted() {
			var seq = 1;
			_.each(this.assessment.assessment_items, ai => {
				ai.sequence = seq;
				seq++;
			});
		},

		canSave() {
			return this.dirty;
		}
	}
};
</script>



// WEBPACK FOOTER //
// src/components/assessment/AssessmentEditDetails.vue